import type { Registry as Services } from '@ember/service';
import { tKey } from 'ember-intl';
import type { MarketSnapshotsKpiTotal } from 'lumen/components/market/snapshots/kpi-card';
import type {
  Kpi,
  MarketPerformanceMetrics,
} from 'lumen/models/market-performance/data-item';
import type MarketPerformanceKpi from 'lumen/models/market-performance/kpi';
import {
  type ComparisonMetricType,
  METRIC_TYPE_TRANSLATIONS,
} from 'lumen/utils/constants/market-snapshots-constants';

export interface KpiCardData {
  title: string;
  total: MarketSnapshotsKpiTotal;
  variance: number | null;
}

interface KpiCardOptions {
  comparisonMetricType: ComparisonMetricType;
}

// A segment card reads one segment of the KPI record instead of its `totals`.
export type KpiCardName =
  | 'group_room_block'
  | 'group_room_nights'
  | 'transient_room_nights'
  | Kpi;

interface KpiCardConfig {
  format: MarketSnapshotsKpiTotal['format'];
  kpi: Kpi;
  source?: 'groupBlk' | 'groupOtb' | 'transient';
  titleKey: string;
}

export const KPI_CARD_CONFIGS: Record<KpiCardName, KpiCardConfig> = {
  adr: { format: 'currency', kpi: 'adr', titleKey: tKey('general.adr') },
  occupancy: {
    format: 'percent',
    kpi: 'occupancy',
    titleKey: tKey('general.occupancy'),
  },
  revenue: {
    format: 'currency',
    kpi: 'revenue',
    titleKey: tKey('general.revenue'),
  },
  revpar: {
    format: 'currency',
    kpi: 'revpar',
    titleKey: tKey('general.revpar'),
  },
  rooms: {
    format: 'decimal',
    kpi: 'rooms',
    titleKey: tKey('general.room_nights'),
  },
  group_room_block: {
    format: 'decimal',
    kpi: 'rooms',
    source: 'groupBlk',
    titleKey: tKey('market.snapshots.kpi.group_room_block'),
  },
  group_room_nights: {
    format: 'decimal',
    kpi: 'rooms',
    source: 'groupOtb',
    titleKey: tKey('market.snapshots.kpi.group_room_nights'),
  },
  transient_room_nights: {
    format: 'decimal',
    kpi: 'rooms',
    source: 'transient',
    titleKey: tKey('market.snapshots.kpi.transient_room_nights'),
  },
};

/**
 * Picks one KPI card's data from the KPI records: the translated title, the
 * OTB total and its variance against the comparison metric type. Missing data
 * is null, never 0.
 */
export function buildKpiCardData(
  intl: Services['intl'],
  kpis: MarketPerformanceKpi[],
  kpiName: KpiCardName,
  { comparisonMetricType }: KpiCardOptions,
): KpiCardData {
  const { format, kpi, source, titleKey } = KPI_CARD_CONFIGS[kpiName];
  const metrics = (
    record: MarketPerformanceKpi | undefined,
  ): MarketPerformanceMetrics | null | undefined =>
    source ? record?.[source] : record?.totals;
  const otb = metrics(kpis.find(({ id }) => id === 'otb'));
  const comparison = metrics(
    kpis.find(({ id }) => id === comparisonMetricType),
  );

  return {
    title: intl.t(titleKey),
    total: { format, value: otb?.[kpi] ?? null },
    variance: comparison?.variance?.[kpi].percent ?? null,
  };
}

// The variance line label shared by the KPI cards, e.g. "vs. SDLY".
export function buildVarianceLabel(
  intl: Services['intl'],
  comparisonMetricType: ComparisonMetricType,
): string {
  return intl.t('general.versus.option', {
    option: intl.t(METRIC_TYPE_TRANSLATIONS[comparisonMetricType]),
  });
}
